import { useLayoutEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { useDOMElement } from 'src/lib/domUtils';

import Backdrop from '../Backdrop';
import { Beacon } from '../Beacon';
import { TooltipFactory } from '../TooltipFactory';

import useStyles from './useStyles';
import { useTooltipFloating } from './useTooltipFloating';

export const Runner = ({
  rootElement,
  step,
  onPause,
  onContinue,
  onNextStep,
  onSkipTour,
  isLastStep,
  stepsCount,
  currentStepIndex,
  dontShowAgain,
  onDontShowAgainChange,
}) => {
  const [tooltipElement, setTooltipElement] = useState(null);

  // Step options
  const options = useMemo(() => step?.options || {}, [step]);
  const backdropVisible = options?.showOverlay || false;

  const styles = useStyles({ zIndex: options.zIndex });

  // Calculate step "counter"
  const counter = useMemo(() => {
    const stepCounter = step?.content?.counter;
    if (stepCounter !== undefined) {
      return stepCounter;
    }

    if (currentStepIndex >= 0 && stepsCount > 0) {
      return `${currentStepIndex + 1}/${stepsCount}`;
    }

    return '';
  }, [step, stepsCount, currentStepIndex]);

  const tooltipTargetElement = useDOMElement(rootElement, step?.target);
  const beaconTargetElement = useDOMElement(rootElement, options.beaconTarget);

  const tooltipFloating = useTooltipFloating({
    referenceElement: tooltipTargetElement,
    floatingElement: tooltipElement,
    options,
  });

  useLayoutEffect(() => {
    if (!tooltipElement) {
      return undefined;
    }

    // Focus on target element
    tooltipTargetElement?.focus();
  }, [tooltipElement, tooltipTargetElement]);

  if (!step) {
    return null;
  }

  return (
    <div
      className={styles.root}
      data-testid="Runner"
    >
      {backdropVisible && (beaconTargetElement || tooltipTargetElement) && (
        <Backdrop
          className={styles.backdrop}
          rootElement={rootElement}
          step={step}
        />
      )}

      {beaconTargetElement && (
        <Beacon
          classes={{ root: styles.beacon }}
          targetElement={beaconTargetElement}
          size={options.beaconSize}
          color={options.beaconColor}
          placement={options.beaconPlacement}
        />
      )}

      {tooltipTargetElement && (
        <TooltipFactory
          className={styles.tooltip}
          ref={setTooltipElement}
          style={tooltipFloating.floatingStyles}
          step={step}
          onPause={onPause}
          onContinue={onContinue}
          onSkipClick={onSkipTour}
          onNextClick={onNextStep}
          isLastStep={isLastStep}
          counter={counter}
          dontShowAgain={dontShowAgain}
          onDontShowAgainChange={onDontShowAgainChange}
        />
      )}
    </div>
  );
};

Runner.propTypes = {
  rootElement: PropTypes.instanceOf(Element).isRequired,
  step: PropTypes.shape({
    target: PropTypes.string,
    options: PropTypes.shape(),
  }).isRequired,
  onPause: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired,
  onNextStep: PropTypes.func.isRequired,
  onSkipTour: PropTypes.func,
  isLastStep: PropTypes.bool,
  stepsCount: PropTypes.number,
  currentStepIndex: PropTypes.number,
  dontShowAgain: PropTypes.bool,
  onDontShowAgainChange: PropTypes.func.isRequired,
};

Runner.defaultProps = {
  onSkipTour: null,
  isLastStep: false,
  stepsCount: 0,
  currentStepIndex: 0,
  dontShowAgain: false,
};
